const DB_NAME    = "pinpoint_offline";
const DB_VERSION = 2;

let dbPromise = null;

function openDB() {
  if (dbPromise) return dbPromise;
  dbPromise = new Promise((resolve, reject) => {
    const req = indexedDB.open(DB_NAME, DB_VERSION);
    req.onupgradeneeded = (e) => {
      const db = e.target.result;

      // ── Existing stores (v1) ─────────────────────────────────────────
      if (!db.objectStoreNames.contains("clients")) {
        db.createObjectStore("clients", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("sections")) {
        db.createObjectStore("sections", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("pending_checkins")) {
        const store = db.createObjectStore("pending_checkins", { keyPath: "localId", autoIncrement: true });
        store.createIndex("clientId", "clientId", { unique: false });
      }
      if (!db.objectStoreNames.contains("pending_notes")) {
        db.createObjectStore("pending_notes", { keyPath: "localId", autoIncrement: true });
      }
      if (!db.objectStoreNames.contains("meta")) {
        db.createObjectStore("meta", { keyPath: "key" });
      }

      // ── New stores (v2) — offline stock ──────────────────────────────
      if (!db.objectStoreNames.contains("stock_items")) {
        const stockStore = db.createObjectStore("stock_items", { keyPath: "id" });
        stockStore.createIndex("vendorId", "vendorId", { unique: false });
      }
      if (!db.objectStoreNames.contains("stock_catalog")) {
        db.createObjectStore("stock_catalog", { keyPath: "id" });
      }
      if (!db.objectStoreNames.contains("pending_sales")) {
        db.createObjectStore("pending_sales", { keyPath: "localId" });
      }
    };
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
  return dbPromise;
}

function tx(storeName, mode, fn) {
  return openDB().then(db => new Promise((resolve, reject) => {
    const transaction = db.transaction(storeName, mode);
    const store = transaction.objectStore(storeName);
    const req = fn(store);
    if (req && req.onsuccess !== undefined) {
      req.onsuccess = () => resolve(req.result);
      req.onerror   = () => reject(req.error);
    } else {
      transaction.oncomplete = () => resolve();
      transaction.onerror    = () => reject(transaction.error);
    }
  }));
}

// ── Clients ───────────────────────────────────────────────────────────────────

export async function saveClientsOffline(clients) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("clients", "readwrite");
    const store = transaction.objectStore("clients");
    store.clear();
    clients.forEach(c => store.put(c));
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
  });
}

export async function getClientsOffline() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("clients", "readonly");
    const store = transaction.objectStore("clients");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// ── Sections ──────────────────────────────────────────────────────────────────

export async function saveSectionsOffline(sections) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("sections", "readwrite");
    const store = transaction.objectStore("sections");
    store.clear();
    sections.forEach(s => store.put(s));
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
  });
}

export async function getSectionsOffline() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("sections", "readonly");
    const store = transaction.objectStore("sections");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// ── Pending check-ins ─────────────────────────────────────────────────────────

export async function savePendingCheckin(checkin) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_checkins", "readwrite");
    const store = transaction.objectStore("pending_checkins");
    const req = store.add({ ...checkin, savedAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function getPendingCheckins() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_checkins", "readonly");
    const store = transaction.objectStore("pending_checkins");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function deletePendingCheckin(localId) {
  return tx("pending_checkins", "readwrite", store => store.delete(localId));
}

// ── Pending notes ─────────────────────────────────────────────────────────────

export async function savePendingNote(note) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_notes", "readwrite");
    const store = transaction.objectStore("pending_notes");
    const req = store.add({ ...note, savedAt: Date.now() });
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function getPendingNotes() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_notes", "readonly");
    const store = transaction.objectStore("pending_notes");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function deletePendingNote(localId) {
  return tx("pending_notes", "readwrite", store => store.delete(localId));
}

// ── Stock items (vendor inventory) ────────────────────────────────────────────

export async function saveStockOffline(stockItems, vendorId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("stock_items", "readwrite");
    const store = transaction.objectStore("stock_items");
    store.clear();
    stockItems.forEach(item => store.put({ ...item, vendorId }));
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
  });
}

export async function getStockOffline(vendorId) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("stock_items", "readonly");
    const store = transaction.objectStore("stock_items");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result.filter(i => i.vendorId === vendorId));
    req.onerror   = () => reject(req.error);
  });
}

export async function updateStockItemOffline(itemId, quantityChange) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("stock_items", "readwrite");
    const store = transaction.objectStore("stock_items");
    const getReq = store.get(itemId);
    getReq.onsuccess = () => {
      const item = getReq.result;
      if (!item) { resolve(0); return; }
      const newQty = Math.max(0, (item.quantity || 0) + quantityChange);
      const putReq = store.put({ ...item, quantity: newQty });
      putReq.onsuccess = () => resolve(newQty);
      putReq.onerror   = () => reject(putReq.error);
    };
    getReq.onerror = () => reject(getReq.error);
  });
}

// ── Stock catalog ─────────────────────────────────────────────────────────────

export async function saveCatalogOffline(catalogItems) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("stock_catalog", "readwrite");
    const store = transaction.objectStore("stock_catalog");
    store.clear();
    catalogItems.forEach(item => store.put(item));
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
  });
}

export async function getCatalogOffline() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("stock_catalog", "readonly");
    const store = transaction.objectStore("stock_catalog");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

// ── Pending sales ─────────────────────────────────────────────────────────────

export async function savePendingSale(sale) {
  const localId = `sale_${Date.now()}_${Math.random().toString(36).slice(2)}`;
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readwrite");
    const store = transaction.objectStore("pending_sales");
    const record = { ...sale, localId, savedAt: Date.now() };
    const req = store.put(record);
    req.onsuccess = () => resolve(record);
    req.onerror   = () => reject(req.error);
  });
}

export async function getPendingSales() {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("pending_sales", "readonly");
    const store = transaction.objectStore("pending_sales");
    const req = store.getAll();
    req.onsuccess = () => resolve(req.result);
    req.onerror   = () => reject(req.error);
  });
}

export async function deletePendingSale(localId) {
  return tx("pending_sales", "readwrite", store => store.delete(localId));
}

// ── Pending counts ────────────────────────────────────────────────────────────

export async function getPendingCount() {
  const [checkins, notes, sales] = await Promise.all([
    getPendingCheckins(),
    getPendingNotes(),
    getPendingSales(),
  ]);
  return checkins.length + notes.length + sales.length;
}

// ── Meta (last sync time, etc.) ───────────────────────────────────────────────

export async function setMeta(key, value) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("meta", "readwrite");
    const store = transaction.objectStore("meta");
    const req = store.put({ key, value });
    req.onsuccess = () => resolve();
    req.onerror   = () => reject(req.error);
  });
}

export async function getMeta(key) {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const transaction = db.transaction("meta", "readonly");
    const store = transaction.objectStore("meta");
    const req = store.get(key);
    req.onsuccess = () => resolve(req.result?.value ?? null);
    req.onerror   = () => reject(req.error);
  });
}

// ── Clear all offline data ────────────────────────────────────────────────────

export async function clearOfflineCache() {
  const db = await openDB();
  const storeNames = [
    "clients", "sections", "pending_checkins", "pending_notes", "meta",
    "stock_items", "stock_catalog", "pending_sales",
  ];
  return new Promise((resolve, reject) => {
    const transaction = db.transaction(storeNames, "readwrite");
    storeNames.forEach(name => transaction.objectStore(name).clear());
    transaction.oncomplete = () => resolve();
    transaction.onerror    = () => reject(transaction.error);
  });
}
